// @material-ui/core components
import Radio from "@material-ui/core/Radio";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormLabel from "@material-ui/core/FormLabel";
import withStyles from "@material-ui/core/styles/withStyles";
import FiberManualRecord from "@material-ui/icons/FiberManualRecord";
import Email from "@material-ui/icons/Email";
import regularFormsStyle from "assets/jss/material-dashboard-pro-react/views/regularFormsStyle";
import axios from 'axios';
import Card from "components/Card/Card.jsx";
import CardBody from "components/Card/CardBody.jsx";
import CardFooter from "components/Card/CardFooter.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardIcon from "components/Card/CardIcon.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import { t } from "i18next";
import React from "react";
import { withTranslation } from "react-i18next";
import { connect } from 'react-redux';
import "react-table/react-table.css";
import { api_url } from 'redux/config.js';
import Toast from 'views/Toast/Toast';


const styles = {
  ...regularFormsStyle,
  descriptionTextArea: {
    width: "100%",
    height: 150
  },
  encryptionGroup: {
    marginTop: 15,
    marginBottom: 10,
  },
  encryptionLabel: {
    fontWeight: 500,
    marginBottom: 10,
    display: 'block',
  },
  helpText: {
    fontSize: '0.8rem',
    color: '#999',
    marginTop: 8,
    marginBottom: 8,
  },
  testButton: {
    marginRight: 10,
  }
};

// Encryption modes → mapping to backend flags
const ENCRYPTION_MODES = {
  ssl:      { secure: true,  requireTLS: false, port: 465, label: 'SSL/TLS (port 465)' },
  starttls: { secure: false, requireTLS: true,  port: 587, label: 'STARTTLS (port 587)' },
  none:     { secure: false, requireTLS: false, port: 25,  label: 'None (port 25)' },
};

function detectMode(smtpData) {
  if (smtpData.secure) return 'ssl';
  if (smtpData.requireTLS) return 'starttls';
  return 'none';
}

class EmailSettingPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      smtpData: {
        user: '',
        host: '',
        port: 465,
        password: '',
        secure: true,
        requireTLS: false,
      },
      encryptionMode: 'ssl',
      testing: false,
      saving: false,
    };
  }

  setSmtpData = (name, value) => {
    let smtpData = { ...this.state.smtpData, [name]: value };
    this.setState({ smtpData });
  }

  setEncryptionMode = (mode) => {
    const cfg = ENCRYPTION_MODES[mode];
    if (!cfg) return;
    // Auto-set port if user hasn't manually customized (or set default)
    const smtpData = {
      ...this.state.smtpData,
      secure: cfg.secure,
      requireTLS: cfg.requireTLS,
      port: cfg.port,
    };
    this.setState({ encryptionMode: mode, smtpData });
  }

  onLoad = () => {
    let self = this;
    const url = api_url + `emailsetting/get`;
    const header = 'Bearer '.concat(this.props.user.token);
    const headers = this.props.user.token ? { Authorization: header } : null;

    this.props.dispatch({ type: 'LOAD_START' });
    axios({ url, method: 'get', headers })
      .then(function (response) {
        if (response.data.success) {
          const smtp = response.data.data ? response.data.data.smtp : null;
          if (smtp) {
            const mode = detectMode(smtp);
            self.setState({ smtpData: smtp, encryptionMode: mode });
          }
        } else {
          Toast(400, response.data.error);
        }
        self.props.dispatch({ type: 'LOAD_END' });
      })
      .catch(function (errors) {
        console.info(errors);
        self.props.dispatch({ type: 'LOAD_END' });
        if (!errors.response) {
          Toast(500, t('Connection error!'));
          return;
        }
        Toast(errors.response.status);
      });
  };

  componentDidMount() {
    this.onLoad();
  }

  verifyForm = () => {
    const s = this.state.smtpData;
    if (!s.user || s.user.length < 1) {
      Toast(400, t('Invalid id or email'));
      return false;
    }
    if (!s.password || s.password.length < 1) {
      Toast(400, t('Invalid password format'));
      return false;
    }
    if (!s.host || s.host.length < 1) {
      Toast(400, t('Invalid hosting address'));
      return false;
    }
    if (!s.port || s.port < 1) {
      Toast(400, t('Invalid port'));
      return false;
    }
    return true;
  }

  // Map backend error types to friendly Greek/English messages
  friendlyError = (data) => {
    const errorType = data.errorType || 'unknown';
    const msg = data.error || 'Unknown error';
    const map = {
      timeout: t('Connection timeout — the SMTP server did not respond. Check host, port, or firewall.'),
      refused: t('Connection refused — check host and port.'),
      dns: t('Host not found — check the SMTP host name for typos.'),
      auth: t('Authentication failed — check the username and password.'),
      tls: t('SSL/TLS error — try a different encryption mode (SSL for 465, STARTTLS for 587).'),
      validation: t('Missing required fields.'),
      db: t('Database error while saving.'),
      unknown: msg,
    };
    return map[errorType] || msg;
  }

  onTest = () => {
    if (!this.verifyForm()) return;
    if (this.state.testing) return;

    this.setState({ testing: true });
    const url = api_url + `emailsetting/test_smtp`;
    const header = 'Bearer '.concat(this.props.user.token);
    const headers = this.props.user.token ? { Authorization: header } : null;
    const data = { smtp: this.state.smtpData };

    // hard client timeout at 15s so the button never hangs forever
    axios({ url, method: 'post', headers, data, timeout: 15000 })
      .then((response) => {
        this.setState({ testing: false });
        if (response.data.success) {
          Toast(200, t('SMTP connection verified successfully'));
        } else {
          Toast(400, this.friendlyError(response.data));
        }
      })
      .catch((err) => {
        this.setState({ testing: false });
        if (err.code === 'ECONNABORTED') {
          Toast(408, t('Test took too long. Server may be blocking outbound SMTP.'));
        } else if (!err.response) {
          Toast(500, t('Connection error!'));
        } else {
          Toast(err.response.status);
        }
      });
  }

  onSave = () => {
    if (!this.verifyForm()) return;
    if (this.state.saving) return;

    this.setState({ saving: true });
    const url = api_url + `emailsetting/save_smtp`;
    const header = 'Bearer '.concat(this.props.user.token);
    const headers = this.props.user.token ? { Authorization: header } : null;
    const data = { smtp: this.state.smtpData };

    axios({ url, method: 'post', headers, data, timeout: 20000 })
      .then((response) => {
        this.setState({ saving: false });
        if (response.data.success) {
          Toast(200, t('Successfully saved'));
        } else {
          Toast(400, this.friendlyError(response.data));
        }
      })
      .catch((err) => {
        this.setState({ saving: false });
        if (err.code === 'ECONNABORTED') {
          Toast(408, t('Save took too long. Server may be blocking outbound SMTP.'));
        } else if (!err.response) {
          Toast(500, t('Connection error!'));
        } else {
          Toast(err.response.status);
        }
      });
  }

  render() {
    const { classes } = this.props;
    const smtpData = this.state.smtpData;
    const { testing, saving, encryptionMode } = this.state;

    return (
      <div>
        <GridContainer>
          <GridItem xs={12} sm={12}>
            <Card>
              <CardHeader color="success" icon>
                <CardIcon color="success">
                  <Email />
                </CardIcon>
              </CardHeader>
              <CardBody>
                <GridContainer>
                  <GridItem xs={12}>
                    <CustomInput
                      labelText={t("Email or UserID")}
                      id="smtp-user"
                      formControlProps={{
                        onChange: (e) => this.setSmtpData('user', e.target.value),
                        fullWidth: true,
                      }}
                      inputProps={{
                        type: "text",
                        value: smtpData.user || '',
                      }}
                    />
                    <CustomInput
                      labelText={t("Password")}
                      id="smtp-password"
                      formControlProps={{
                        onChange: (e) => this.setSmtpData('password', e.target.value),
                        fullWidth: true,
                      }}
                      inputProps={{
                        type: "password",
                        value: smtpData.password || '',
                      }}
                    />
                    <CustomInput
                      labelText={t("Smtp host")}
                      id="smtp-host"
                      formControlProps={{
                        onChange: (e) => this.setSmtpData('host', e.target.value),
                        fullWidth: true,
                      }}
                      inputProps={{
                        type: "text",
                        value: smtpData.host || '',
                      }}
                    />
                    <CustomInput
                      labelText={t("Smtp port")}
                      id="smtp-port"
                      formControlProps={{
                        onChange: (e) => this.setSmtpData('port', parseInt(e.target.value, 10) || 0),
                        fullWidth: true,
                      }}
                      inputProps={{
                        type: "number",
                        value: smtpData.port || 0,
                      }}
                    />
                  </GridItem>

                  <GridItem xs={12} className={classes.encryptionGroup}>
                    <FormLabel component="legend" className={classes.encryptionLabel}>
                      {t('Encryption')}
                    </FormLabel>
                    {Object.keys(ENCRYPTION_MODES).map(key => (
                      <FormControlLabel
                        key={key}
                        control={
                          <Radio
                            checked={encryptionMode === key}
                            onChange={() => this.setEncryptionMode(key)}
                            value={key}
                            name="encryption-mode"
                            icon={<FiberManualRecord className={classes.radioUnchecked} />}
                            checkedIcon={<FiberManualRecord className={classes.radioChecked} />}
                            classes={{ checked: classes.radio, root: classes.radioRoot }}
                          />
                        }
                        classes={{ label: classes.label }}
                        label={t(ENCRYPTION_MODES[key].label)}
                      />
                    ))}
                    <div className={classes.helpText}>
                      {t('SSL: usually port 465. STARTTLS: usually port 587. None: port 25 (not recommended).')}
                    </div>
                  </GridItem>
                </GridContainer>
              </CardBody>
              <CardFooter>
                <Button
                  color="warning"
                  className={classes.testButton}
                  onClick={() => this.onTest()}
                  disabled={testing || saving}
                >
                  {testing ? t('Testing...') : t('Test Connection')}
                </Button>
                <Button
                  color="info"
                  onClick={() => this.onSave()}
                  disabled={testing || saving}
                >
                  {saving ? t('Saving...') : t('Save')}
                </Button>
              </CardFooter>
            </Card>
          </GridItem>
        </GridContainer>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({ user: state.user });

export default connect(mapStateToProps)(withTranslation()(withStyles(styles)(EmailSettingPage)));
